/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextInput from 'components/input/textInput';
import DatePickerInput from 'components/input/DatePicker';
import { dateFormat } from 'components/helper/helper';
import moment from 'moment';
import GroupRadioButtonRounded from 'components/input/GroupRadioButtonRounded';
import AutocompleteInput from 'components/input/AutoCompleteInput';
import YesNoSwitch from 'components/input/YesNoSwitch';
import GroupCheckbox from 'components/input/GroupCheckbox';
import Grid from '@mui/joy/Grid';
import { cmSubjectTestOption } from 'screen/course/courseConstant';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface Props {
  handleSave: (value: any) => void;
  item: any;
  index: any;
  indexText: string;
  key: number;
  check?: any;
  indexExpand?: any;
  expandAll?: boolean
  handleExpandActive: (value: any) => void;
  isMobile?: boolean;
  disabled?: boolean;
  // True when 15 other tests already include Common App — keep this card's
  // Common App checkbox off and disabled so the student can still add UC/CSU tests.
  commonAppLimitReached?: boolean;
}

// Plain option names for the "AP exam" Autocomplete dropdown.
// cmSubjectTestOption now stores { optionName, CommonApp, UC, CSU } instead of plain strings.
const cmSubjectTestOptionNames = cmSubjectTestOption.map((option) => option.optionName);

const APSubjectConfig = (props: Props) => {
  const { check, 
    // isMobile = false, 
    disabled = false, commonAppLimitReached = false } = props;
  const saveRef = React.useRef(props.handleSave);
  saveRef.current = props.handleSave;

  const validationSchema = Yup.object().shape({
    subjectCM: Yup.string().nullable().required('Field is required'),
    examStatus: Yup.string().nullable().required('Field is required'),
    dateTaken: Yup.string().nullable()
      .when('examStatus', {
        is: 'Completed',
        then: Yup.string().nullable().required('Date is required')
            .test(
              'is-valid-date',
              'Invalid date',
              function (value:any) {
                if (!value) {
                  return false;
                }

                let year = moment(value).format('yyyy');
                if (Number(year)<2000) return false;

                return moment(value).isValid();
              }
            ),
      }),
    planToTakeExam: Yup.string().nullable()
    .when('examStatus', {
        is: 'Planned',
        then: Yup.string().nullable().required('Date is required')
            .test(
              'is-valid-date',
              'Invalid date',
              function (value:any) {
                if (!value) {
                  return false;
                }

                return moment(value).isValid();
              }
            ),
      }),
  });

  const formItem = useFormik({
    initialValues: {
      isReceivedScore: '',
      examStatus: 'Planned',
      subject: '',
      subjectCM: '',
      dateTaken: null,
      dateTakenCSUApp: '',
      dateTakenMonth: '',
      dateTakenYear: '',
      score: '',
      planToTakeExamMonth: '',
      planToTakeExamYear: '',
      planToTakeExam: '',
      isIncludeIntoCommonApp: false,
      isIncludeIntoUCApp: false,
      isIncludeIntoCSUApp: false,
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      saveRef.current({ ...values });
    },
  });
  const [expanded, setExpanded] = React.useState(props.expandAll || false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
    props.handleExpandActive(expanded);
  };

  const elementRef = React.useRef<HTMLDivElement>(null);

  // Flags (Common App / UC / CSU) for the currently selected AP exam, used to
  // auto check/uncheck and enable/disable the "Include test info" checkboxes below.
  const selectedSubjectTest = React.useMemo(
    () => cmSubjectTestOption.find((option) => option.optionName === formItem.values.subjectCM),
    [formItem.values.subjectCM]
  );

  // When the AP exam changes: set each app checkbox to match the exam's supported
  // apps, so Common App/UC/CSU stay in sync with what that exam actually accepts.
  const handleSubjectCMChange = (newValue: any) => {
    formItem.handleChange(newValue);
    const nextSelected = cmSubjectTestOption.find((option) => option.optionName === newValue);
    if (nextSelected) {
      formItem.setFieldValue(
        'isIncludeIntoCommonApp',
        nextSelected.CommonApp && !commonAppLimitReached
      );
      formItem.setFieldValue('isIncludeIntoUCApp', nextSelected.UC);
      formItem.setFieldValue('isIncludeIntoCSUApp', nextSelected.CSU);
    }
  };

  // Safety net for both initial load (existing saved data) and manual changes:
  // whenever the selected AP exam does not accept an app, force that app's
  // checkbox back to false so a disabled checkbox never stays checked.
  React.useEffect(() => {
    if (!selectedSubjectTest) return;
    if (
      formItem.values.isIncludeIntoCommonApp &&
      (!selectedSubjectTest.CommonApp || commonAppLimitReached)
    ) {
      formItem.setFieldValue('isIncludeIntoCommonApp', false);
    }
    if (!selectedSubjectTest.UC && formItem.values.isIncludeIntoUCApp) {
      formItem.setFieldValue('isIncludeIntoUCApp', false);
    }
    if (!selectedSubjectTest.CSU && formItem.values.isIncludeIntoCSUApp) {
      formItem.setFieldValue('isIncludeIntoCSUApp', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubjectTest, commonAppLimitReached]);

  React.useEffect(() => {
    if (check && props.indexExpand && props.indexExpand === props.indexText) {
      setExpanded(true);

      setTimeout(() => {
        if (elementRef.current) {
          elementRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    } else {
      setExpanded(props.expandAll || false);
    }
    if (props.item) formItem.resetForm({ values: { ...formItem.values, ...props.item } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.expandAll]);

  React.useEffect(() => {
    if (formItem.dirty) {
      saveRef.current({ ...formItem.values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItem.values]);


  const renderFillAppStatus = () => {
    return (
      <span className='mt-2' style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
        {formItem.values.isIncludeIntoCommonApp && (
          <img
            alt="cm"
            src="/assets/icons/icon-cm.svg"
            style={{ marginTop: '-8px', marginLeft: '4px' }}
          />
        )}
        {formItem.values.isIncludeIntoUCApp && (
          <img
            alt="uc"
            src="/assets/icons/icon-uc.svg"
            style={{ marginTop: '-8px', marginLeft: '4px' }}
          />
        )}
        {formItem.values.isIncludeIntoCSUApp && (
          <img
            alt="csu"
            src="./assets/icons/icon-csu-5.svg"
            style={{ marginTop: '-13px', marginLeft: '4px' }}
          />
        )}
      </span>
    );
  };

  return (
    <Card className="w-100 my-3 px-0" key={props.indexText} ref={elementRef}>
      <CardActions
        disableSpacing
        onClick={handleExpandClick}
        sx={{
          backgroundColor: expanded ? '#AAEBF7' : 'none',
          borderBottom: expanded ? '2px solid #4D88F0' : 'none',
          marginLeft: '-8px',
        }}
      >
        <div className="w-100 d-flex align-items-center">
          <span className={`p-1 ps-3 flex-grow-1`}>
            {formItem.values.subjectCM && `AP ${formItem.values.subjectCM}`}
            {formItem.values.examStatus === 'Completed' ? (
              <>
                {formItem.values.dateTaken && (
                  <> - ({moment(formItem.values.dateTaken).format(dateFormat)})</>
                )}
                {formItem.values.score && <> - Score: {formItem.values.score}</>}
              </>
            ) : formItem.values.planToTakeExam ? (
              <> - ({moment(formItem.values.planToTakeExam).format(dateFormat)})</>
            ) : null}
          </span>
          {renderFillAppStatus()}
          <strong className="text-field-light mt-1 me-3" style={{ minWidth: '40px', textAlign: 'right' }}>{props.indexText}</strong>
        </div>
        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          id={`btn_fold_${props.indexText.replace("/","_")}`}
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          <form id={`SubjectConfig_${props.index}`} onSubmit={formItem.handleSubmit}>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <>
              <AutocompleteInput
                id={`subjectCM_${props.index+1}`}
                className="common-flag end-flag"
                form={formItem}
                option={cmSubjectTestOptionNames}
                label="AP exam *"
                dataUp={`ap-exam-${props.index + 1}`}
                name="subjectCM"
                values={formItem.values.subjectCM}
                onChange={handleSubjectCMChange}
                isMissing={check && !formItem.values.subjectCM}
                disabled={disabled}
              />
              {/* {formItem.errors.subjectCM && formItem.touched.subjectCM && (
                  <div className="text-danger">{formItem.errors.subjectCM}</div>
              )} */}
              </>
            </Grid>

            <Grid xs={12}>
              <GroupRadioButtonRounded
                name="examStatus"
                label="Is this exam completed or planned?"
                values={formItem.values.examStatus}
                onChange={formItem.handleChange}
                options={[{ value: 'Completed' }, { value: 'Planned' }]}
                isMissing={check && !formItem.values.examStatus}
                disabled={disabled}
              />
              {check && formItem.errors.examStatus && formItem.touched.examStatus && (
                <div className="text-danger">{formItem.errors.examStatus}</div>
              )}
            </Grid>

            {formItem.values.examStatus === 'Completed' ? (
              <>
                <Grid xs={12} md={6}>
                  <>
                  <DatePickerInput
                    id={`dateTaken_${props.index}`}
                    label={'When did you take this test? *'}
                    name="dateTaken"
                    values={formItem.values.dateTaken}
                    onChange={(value) => {
                      formItem.setFieldValue('dateTaken', value);
                    }}
                    isMissing={check && !formItem.values.dateTaken}
                    disabled={disabled}
                  />
                {check && formItem.errors.dateTaken && formItem.touched.dateTaken && (
                    <div className="text-danger">{formItem.errors.dateTaken}</div>
                )}
                  </>
                </Grid>

                <Grid xs={12} md={6}>
                  <YesNoSwitch
                    id={`isReceivedScore_${props.index+1}`}
                    className="mb-3"
                    label="Have you received your score yet? *"
                    name="isReceivedScore"
                    values={formItem.values.isReceivedScore}
                    onChange={(e: any) => {
                      formItem.handleChange(e);
                      if (e.target.value === 'false') {
                        formItem.setFieldValue('score', '');
                      }
                    }}
                    isMissing={check && !formItem.values.isReceivedScore}
                    disabled={disabled}
                  />
                {check && formItem.errors.isReceivedScore && formItem.touched.examStatus && (
                  <div className="text-danger">{formItem.errors.isReceivedScore}</div>
                )}
                </Grid>

                {formItem.values.isReceivedScore === 'true' && (
                  <Grid xs={12} md={6}>
                    <TextInput
                      id={`score_${props.index}`}
                      label="Score *"
                      name="score"
                      type="number"
                      max={5}
                      min={1}
                      values={formItem.values.score}
                      onChange={formItem.handleChange}
                      isMissing={check && !formItem.values.score}
                      disabled={disabled}
                    />
                  </Grid>
                )}
              </>
            ) : (
              <Grid xs={12} md={6}>
                <>
                <DatePickerInput
                  label="When do you plan to take the exam? *"
                  name="planToTakeExam"
                  values={formItem.values.planToTakeExam}
                  onChange={(value) => formItem.setFieldValue('planToTakeExam', value)}
                  isMissing={check && !formItem.values.planToTakeExam}
                  disabled={disabled}
                />
              {check && formItem.errors.planToTakeExam && formItem.touched.planToTakeExam && (
                  <div className="text-danger">{formItem.errors.planToTakeExam}</div>
              )}
                </>
              </Grid>
            )}

            <Grid xs={12}>
              <GroupCheckbox
                label="Include test info into these applications"
                form={formItem}
                mdCol={3}
                columns={3}
                options={[
                  {
                    value: 'isIncludeIntoCommonApp',
                    label: 'Common App',
                    // Disabled when the exam is not accepted by Common App, or
                    // when 15 other tests already use the Common App slot.
                    disabled:
                      commonAppLimitReached ||
                      (!!selectedSubjectTest && !selectedSubjectTest.CommonApp),
                    onChecked: (checked: boolean) => {
                      saveRef.current({
                        ...formItem.values,
                        isIncludeIntoCommonApp: checked,
                      });
                    },
                  },
                  {
                    value: 'isIncludeIntoUCApp',
                    label: 'UC App',
                    // Disabled when the selected AP exam is not accepted by UC
                    disabled: !!selectedSubjectTest && !selectedSubjectTest.UC,
                  },
                  {
                    value: 'isIncludeIntoCSUApp',
                    label: 'CSU App',
                    // Disabled when the selected AP exam is not accepted by CSU
                    disabled: !!selectedSubjectTest && !selectedSubjectTest.CSU,
                  },
                ]}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={9}></Grid>
            <Grid xs={12} md={3}>
              <Button
                className="w-100 btn-save-info"
                onClick={() => {
                  formItem.handleSubmit();
                  if (formItem.isValid) {
                    setExpanded(false);
                  }
                }}
                disabled={disabled}
              >
                Save Info
              </Button>
            </Grid>
          </Grid>
          </form>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default APSubjectConfig;
